import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import PageHeader from "../../components/layout/PageHeader";
import { useFirestore, useFirestoreID } from "../../firebase/useFirestore";
import useMechanismProposals from "../../components/storytelling/useMechanismProposals";
import MechanismProposalList from "../../components/storytelling/MechanismProposalList";
import buildAcceptedPayload, { NEW_ENTITY_VALUE } from "../../helpers/mechanisms/buildAcceptedPayload";
import Location from "../../hooks/Location";
import { client } from "../../hooks/Client";
import spinner from "../../images/spinner-ripple.svg";

// Review screen for Decca's proposed mechanisms of change. Decca proposes on
// mount (read-only); the user edits the label, re-points the activity/effect
// and includes/excludes each code, then saves. Nothing is written until save.
const MechanismReview = () => {
    // Hooks
    const interviewId = Location()[3];
    const history = useHistory();
    const { proposals, status, error, propose, save } = useMechanismProposals(interviewId);

    // Firestore — tenant TOC for the re-point dropdowns
    const interviews = useFirestoreID("StorytellingInterviews", interviewId || "");
    const interview = interviews && interviews[0];
    const targetgroups = useFirestore("Stakeholders") || [];
    const activities = useFirestore("Activities") || [];
    const effects = useFirestore("OutputEffects") || [];
    const msiEffects = effects.filter((effect) => effect.Standard === "true" || effect.MSIId);

    // Editable copy of the proposals
    const [rows, setRows] = useState([]);
    const [saveResult, setSaveResult] = useState(null);

    // Propose once on mount.
    useEffect(() => {
        propose();
    }, [propose]);

    // Seed editable rows when proposals arrive. `title` is the concrete
    // mechanism (verbatim from the taxonomy, or the tenant's existing entry);
    // `mechanismId` marks a context-adding row for an existing mechanism.
    useEffect(() => {
        setRows(
            proposals.map((p) => ({
                label: p.title,
                category: p.category || "",
                mechanismId: p.mechanismId || null,
                evidence: p.evidence,
                explanation: p.explanation || "",
                // A proposed NEW doelgroep/activiteit (standalone tenants only)
                // pre-selects the "nieuw" option; the user can switch to an
                // existing one or leave it out.
                targetgroupId: p.suggestedTargetgroupId || (p.newTargetgroupLabel ? NEW_ENTITY_VALUE : ""),
                newTargetgroupLabel: p.newTargetgroupLabel || "",
                activityId: p.suggestedActivityId || (p.newActivityLabel ? NEW_ENTITY_VALUE : ""),
                newActivityLabel: p.newActivityLabel || "",
                effectId: p.suggestedEffectId || "",
                isNew: p.isNew,
                accepted: true,
            }))
        );
    }, [proposals]);

    const updateRow = (index, patch) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const handleSave = async () => {
        const accepted = buildAcceptedPayload(rows);
        if (accepted.length === 0) return;
        const result = await save(accepted);
        setSaveResult(result);
    };

    const goBack = () => history.push(`/${client}/storytellinginterviewdetail/${interviewId}`);

    const acceptedCount = rows.filter((r) => r.accepted).length;

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={"Werkzame elementen"}
                    description={
                        interview
                            ? `Voorgestelde mechanismen uit "${interview.Title || "dit interview"}". Controleer, pas aan en sla op.`
                            : "Voorgestelde mechanismen uit dit interview."
                    }
                    AI={true}
                />

                {status === "proposing" && (
                    <div className="dashboard-container">
                        <div className="mechanism-review-status">
                            <span className="privacy-step-spinner" />
                            <p>Decca leest het interview en stelt werkzame elementen voor…</p>
                        </div>
                    </div>
                )}

                {status === "error" && (
                    <div className="dashboard-container">
                        <p className="mechanism-review-error">Er ging iets mis: {error}</p>
                        <button className="button-simple" onClick={propose}>
                            Opnieuw proberen
                        </button>
                    </div>
                )}

                {status === "saved" && (
                    <div className="dashboard-container">
                        <p className="mechanism-review-done">
                            ✓ {saveResult?.saved?.length || 0} werkzame elementen opgeslagen.
                        </p>
                        <button className="button-simple" onClick={goBack}>
                            Terug naar het interview
                        </button>
                    </div>
                )}

                {(status === "ready" || status === "saving") && rows.length === 0 && (
                    <div className="dashboard-container">
                        <p className="mechanism-review-empty">
                            Decca vond geen concrete werkzame elementen in dit interview.
                        </p>
                        <button className="button-simple" onClick={goBack}>
                            Terug naar het interview
                        </button>
                    </div>
                )}

                {(status === "ready" || status === "saving") && rows.length > 0 && (
                    <div className="dashboard-container">
                        <MechanismProposalList
                            rows={rows}
                            updateRow={updateRow}
                            targetgroups={targetgroups}
                            activities={activities}
                            msiEffects={msiEffects}
                        />

                        <div className="mechanism-review-actions">
                            {status === "saving" ? (
                                <img src={spinner} alt="loading spinner" />
                            ) : (
                                <button
                                    className="button-simple"
                                    onClick={handleSave}
                                    disabled={acceptedCount === 0}
                                >
                                    {acceptedCount} opslaan
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MechanismReview;
